import Button from '@/components/Button'
import Icon from '@/components/Icon'
import { faFacebookF, faInstagram, faLinkedinIn, faXTwitter } from '@fortawesome/free-brands-svg-icons'
import { faArrowRight } from '@fortawesome/sharp-solid-svg-icons'
import Image from 'next/image'
import Link from 'next/link'
import styles from './LeaderProfile.module.scss'

// Maps the ACF `platform` select values to their brand icon. A platform with no
// entry here renders nothing, so adding a choice in ACF without adding it below
// fails quietly rather than breaking the page.
const SOCIAL_ICONS = {
  linkedin: faLinkedinIn,
  facebook: faFacebookF,
  x: faXTwitter,
  instagram: faInstagram
}

const SOCIAL_LABELS = {
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  x: 'X',
  instagram: 'Instagram'
}

function normalisePlatform(platform) {
  const value = Array.isArray(platform) ? platform[0] : platform
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

export default function LeaderProfile(props) {
  const { eyebrow, leaderName, intro, headshot, connectLink, bioHeading, bio, backgroundImage, socialLinks } = props.fields ?? {}

  // Defaults to 2 for use alongside a page template that renders its own h1. A template
  // that shows this instead of its header should pass 1.
  const nameLevel = Math.min(6, Math.max(1, Math.round(Number(props.headingLevel)) || 2))
  const NameTag = `h${nameLevel}`

  const headshotUrl = headshot?.node?.sourceUrl
  // Falls back to the shared dark texture so a profile still looks intentional
  // before an editor uploads a bespoke background.
  const backgroundUrl = backgroundImage?.node?.sourceUrl || '/dark-background.webp'
  // The ACF select is multi-value, so `platform` arrives as an array. Reading it directly
  // only worked because a one-element array stringifies to its element; two selections
  // would have produced 'x,facebook' and dropped the icon silently. Case is normalised for
  // the same reason — 'Linkedin' is a valid-looking choice that matched nothing.
  const socials = (socialLinks ?? [])
    .map((social) => ({ ...social, platformKey: normalisePlatform(social?.platform) }))
    .filter((social) => social?.url && SOCIAL_ICONS[social.platformKey])

  return (
    <article className={styles.leaderProfile}>
      <div className={styles.hero}>
        <Image
          className={styles.heroBackground}
          src={backgroundUrl}
          alt={backgroundImage?.node?.altText || ''}
          fill
          sizes="100vw"
          preload
          fetchPriority="high"
        />

        <div className={styles.heroInner}>
          <div className={styles.heroText}>
            {eyebrow && <p className={`eyebrow-text ${styles.eyebrow}`}>{eyebrow}</p>}
            {leaderName && <NameTag className={styles.name}>{leaderName}</NameTag>}
            {intro && <p className={styles.intro}>{intro}</p>}
          </div>

          {headshotUrl && (
            <div className={styles.headshotWrapper}>
              <Image
                className={styles.headshot}
                src={headshotUrl}
                alt={headshot?.node?.altText || leaderName || ''}
                width={540}
                height={540}
                sizes="(max-width: 768px) 80vw, 380px"
                priority
              />
              {connectLink?.url && (
                <Button className={styles.connect} href={connectLink.url} target={connectLink.target}>
                  {connectLink.title || "Let's Connect"} <Icon icon={faArrowRight} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {(bio || socials.length > 0) && (
        <div className={styles.bioSection}>
          <div className={styles.bioInner} data-aos="fade-up">
            {bioHeading && <h2 className={styles.bioHeading}>{bioHeading}</h2>}
            {bio && <div className={styles.bio} dangerouslySetInnerHTML={{ __html: bio }} />}

            {socials.length > 0 && (
              <ul className={styles.socials}>
                {socials.map((social, index) => (
                  // Index included because nothing stops an editor adding two rows for the
                  // same platform, which would collide on platformKey alone.
                  <li key={`${social.platformKey}-${index}`}>
                    <Link
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${leaderName ? `${leaderName} on ` : ''}${SOCIAL_LABELS[social.platformKey]}`}
                    >
                      <Icon icon={SOCIAL_ICONS[social.platformKey]} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </article>
  )
}
